import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';

export const AboutPage = () => {
    return (
        <Layout>
            {/* Hero */}
            <section className="min-h-[60vh] flex items-end pb-16 pt-32 px-4 md:px-8 relative overflow-hidden">
                <div className="noise-overlay" />
                <div className="max-w-[1400px] mx-auto w-full">
                    <motion.span initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.1}}
                        className="text-xs tracking-[0.2em] uppercase text-lime mb-4 block">
                        Born in Mumbai
                    </motion.span>
                    <motion.h1 initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{delay:0.2}}
                        className="font-heading text-5xl md:text-7xl lg:text-9xl leading-none mb-6">
                        THE FUTURE OF<br />
                        <span className="text-lime">INDIAN</span><br />
                        STREET STYLE
                    </motion.h1>
                    <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.3}}
                        className="text-muted-foreground text-lg max-w-2xl">
                        India's premier urban streetwear destination — built for a generation that refuses to blend in.
                    </motion.p>
                </div>
            </section>

            {/* Mission Statement */}
            <section className="py-16 md:py-24 px-4 md:px-8 border-t border-border">
                <div className="max-w-[1400px] mx-auto">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <span className="text-xs tracking-[0.2em] uppercase text-lime mb-4 block">Our Mission</span>
                            <h2 className="font-heading text-4xl md:text-5xl mb-6">WE DON'T JUST DESIGN CLOTHES.<br />WE CREATE A MOVEMENT.</h2>
                        </div>
                        <div>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                YESSuhchill is India's premier urban streetwear destination — an independent brand built for a generation that refuses to blend in. From the heart of Mumbai to the global stage, YESSuhchill is redefining the landscape of homegrown Indian streetwear.
                            </p>
                            <p className="text-muted-foreground leading-relaxed">
                                We don't just design clothes; we create a movement for the bold, the real, and the unapologetically original.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Story */}
            <section className="py-16 md:py-24 px-4 md:px-8 bg-card border-t border-border">
                <div className="max-w-[1400px] mx-auto">
                    <span className="text-xs tracking-[0.2em] uppercase text-lime mb-4 block">Our Story</span>
                    <h2 className="font-heading text-4xl md:text-5xl mb-8">BUILT FROM THE<br />GROUND UP</h2>
                    <div className="grid md:grid-cols-2 gap-12">
                        <p className="text-muted-foreground leading-relaxed">
                            Founded by <span className="text-white font-medium">Joshua Peddarayadu</span> and <span className="text-white font-medium">Joseph Peddavalli</span>, YESSuhchill was born from a singular mission: to provide an authentic alternative to mass-market fast fashion. We realized that Indian youth were tired of "copy-paste" Western designs.
                        </p>
                        <p className="text-muted-foreground leading-relaxed">
                            We set out to build a brand that speaks the local language — raw, original, and fueled by the relentless energy of Mumbai's streets. We aren't just another label; we are a response to the status quo. We're hungry, we're growing, and we're just getting started.
                        </p>
                    </div>
                </div>
            </section>

            {/* What Defines Us */}
            <section className="py-16 md:py-24 px-4 md:px-8 border-t border-border">
                <div className="max-w-[1400px] mx-auto">
                    <span className="text-xs tracking-[0.2em] uppercase text-lime mb-4 block">What Defines Us</span>
                    <h2 className="font-heading text-4xl md:text-5xl mb-12">THE YESSUHCHILL DNA</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { title: 'AUTHENTIC DNA', desc: 'Every design is a story. High-quality fabrics and unique silhouettes that reflect the grit and glamour of urban India.' },
                            { title: 'ENGINEERED FOR THE STREETS', desc: 'Designed for the Indian climate and lifestyle. Oversized fits, heavyweight tees — comfort without compromising on edge.' },
                            { title: 'THE CHILL SQUAD', desc: 'More than a brand — a community. We represent the underground artists, the dreamers, and the hustlers who make our culture.' },
                            { title: 'PREMIUM QUALITY', desc: 'No shortcuts. No settling. Streetwear that stands the test of time.' },
                        ].map((item, i) => (
                            <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.1}}
                                className="border border-border p-6 hover:border-lime transition-colors">
                                <div className="text-lime text-2xl font-heading mb-3">0{i+1}</div>
                                <h3 className="font-heading text-sm tracking-wider mb-3">{item.title}</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Founders */}
            <section className="py-16 md:py-24 px-4 md:px-8 bg-card border-t border-border">
                <div className="max-w-[1400px] mx-auto">
                    <span className="text-xs tracking-[0.2em] uppercase text-lime mb-4 block">The Visionaries</span>
                    <h2 className="font-heading text-4xl md:text-5xl mb-12">MEET THE FOUNDERS</h2>
                    <div className="grid md:grid-cols-2 gap-8">
                        {[
                            { name: 'JOSHUA PEDDARAYADU', role: 'Co-Founder' },
                            { name: 'JOSEPH PEDDAVALLI', role: 'Co-Founder' },
                        ].map((founder, i) => (
                            <div key={i} className="border border-border p-8 hover:border-lime transition-colors">
                                <div className="w-16 h-16 bg-lime/10 border border-lime/30 flex items-center justify-center mb-6">
                                    <span className="text-lime font-heading text-2xl">{founder.name[0]}</span>
                                </div>
                                <h3 className="font-heading text-2xl mb-2">{founder.name}</h3>
                                <span className="text-xs tracking-[0.2em] uppercase text-lime">{founder.role}</span>
                                <p className="text-muted-foreground text-sm mt-4 leading-relaxed">
                                    Visionary behind YESSuhchill. With a shared passion for street culture and design, committed to putting Indian streetwear on the global map through pure grind and uncompromising style.
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 md:py-24 px-4 md:px-8 border-t border-border text-center">
                <div className="max-w-[800px] mx-auto">
                    <span className="text-xs tracking-[0.2em] uppercase text-lime mb-4 block">Join The Movement</span>
                    <h2 className="font-heading text-4xl md:text-6xl mb-6">THE CHILL ONES KNOW.</h2>
                    <p className="text-muted-foreground mb-8">Don't just follow the trend — be the trend. The first collection is loading.</p>
                    <Link to="/products" className="inline-block bg-lime text-black px-8 py-4 text-xs font-bold tracking-[0.14em] uppercase hover:bg-lime/90 transition-colors">
                        SHOP NOW
                    </Link>
                </div>
            </section>
        </Layout>
    );
};
